import React, { useMemo } from 'react';
import { useState, useEffect } from 'react';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import styled from 'styled-components';
import { P, LabelUppercase, PSmall, H3, PExtraSmall } from '../elements/Text';
import {
  CompanyResponse,
  LearningPlanItem,
  SubmissionResponse,
  saveOfficeHoursNotes,
} from '../../api/admin';
import Checkbox from '../elements/Checkbox';
import { MultiLineInput } from '../elements/Input';
import { useAutoAnimate } from '@formkit/auto-animate/react';
import Button from '../elements/Button';
import { LEARNING_PLAN_UNIT_TYPE } from '../LearningPlan/LearningPlanUnit';
import { TOPIC_TO_COLOR } from '../../theme';
import { TopicPill, TopicIcon, TopicText } from './TopicPill';
import { SmallPortalTooltip, SmallTooltip } from '../Tooltip';
import {
  useGetCompany,
  useGetCompanyOfficeHours,
  useGetExerciseSubmissions,
} from '../../hooks/use-hydra/admin';
import { useParams, Prompt } from 'react-router-dom';
import { useMutation } from 'react-query';
import useAuth from '../../hooks/use-auth';
import { toast } from 'react-toastify';
import { TooltipContent, TooltipDetail } from './Progress/Tooltip';

dayjs.extend(utc);
dayjs.extend(timezone);

const MentorName = styled(P)`
  font-weight: 600;
  color: ${({ theme }) => theme.colors.textPrimary};
  margin: 0;
  padding: 0;
  font-size: 18px;
`;

const MentorLabel = styled(LabelUppercase)`
  color: ${({ theme }) => theme.colors.textRegular} !important;
  margin: 0;
  padding: 0;
  font-size: 11px;
  font-weight: 500;
`;

// Modern card-based layout
const LearnersContainer = styled.div`
  display: flex;
  gap: 24px;
  overflow-x: auto;
  overflow-y: visible;
  padding: 32px 64px;
  min-height: min-content;

  &::-webkit-scrollbar {
    height: 8px;
  }

  &::-webkit-scrollbar-track {
    background: ${({ theme }) => theme.colors.bgActive};
    border-radius: 4px;
  }

  &::-webkit-scrollbar-thumb {
    background: ${({ theme }) => theme.colors.textRegular};
    border-radius: 4px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: ${({ theme }) => theme.colors.textPrimary};
  }
`;

const LearnerCard = styled.div`
  width: 500px;
  border: 1px solid ${({ theme }) => theme.colors.bgActive};
  border-radius: 12px;
  padding: 0;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
  transition: all 0.2s ease-in-out;
  flex-shrink: 0;

  &:hover {
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.08);
  }
`;

const LearnerHeader = styled.div`
  padding: 20px 24px;
  background: ${({ theme }) => theme.colors.bgPage};
  border-bottom: 1px solid ${({ theme }) => theme.colors.bgActive};
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const LearnerNameRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 16px;
`;

const LearnerContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0;
`;

// Redesigned sections with better visual hierarchy
const SectionContainer = styled.div`
  border-bottom: 1px solid ${({ theme }) => theme.colors.bgActive};

  &:last-child {
    border-bottom: none;
  }
`;

const SectionHeader = styled.div`
  padding: 16px 24px 12px;
  background: ${({ theme }) => theme.colors.bgDefaultSecondary};
  border-bottom: 1px solid ${({ theme }) => theme.colors.bgActive};
`;

const SectionTitle = styled(LabelUppercase)`
  color: ${({ theme }) => theme.colors.textPrimary};
  margin: 0;
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0.5px;
`;

const SectionContent = styled.div`
  padding: 20px 24px;
  background: ${({ theme }) => theme.colors.bgPage};
`;

// Attendance section styling
const AttendanceContent = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const CheckboxLabel = styled(PSmall)`
  margin: 0;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.textPrimary};
  font-size: 14px;
  padding: 0;
`;

// Notes section with compact design
const NotesGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const NoteField = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const NoteLabel = styled(LabelUppercase)`
  color: ${({ theme }) => theme.colors.textRegular};
  margin: 0;
  font-size: 9px;
  font-weight: 600;
  letter-spacing: 0.5px;
`;

const CompactTextArea = styled(MultiLineInput)`
  font-size: 13px;
  padding: 0;
  margin: 0;

  div {
    padding: 0;
  }

  textarea {
    font-size: 13px;
    line-height: 1.4;
    padding: 14px 16px;
  }
`;

// Collapsible sections with modern design
const CollapsibleSectionContainer = styled.div`
  border-bottom: 1px solid ${({ theme }) => theme.colors.bgActive};

  &:last-child {
    border-bottom: none;
  }
`;

const CollapsibleHeader = styled.div<{ expanded: boolean }>`
  background-color: ${({ theme }) => theme.colors.bgDefaultSecondary};
  padding: 16px 24px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  cursor: pointer;
  gap: 16px;
  transition: all 0.2s ease-in-out;
  color: ${({ theme }) => theme.colors.textPrimary};

  &:hover {
    background-color: ${({ theme }) => theme.colors.bgActive};
  }
`;

const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const ToggleIcon = styled.i`
  width: 16px;
  display: inline-flex;
  justify-content: center;
  align-items: center;
  font-size: 12px;
  color: ${({ theme }) => theme.colors.textRegular};
  transition: transform 0.2s ease-in-out;
  transform: ${({ 'aria-expanded': expanded }) =>
    expanded ? 'rotate(0deg)' : 'rotate(-90deg)'};
`;

const CollapsibleContent = styled.div`
  padding: 20px 24px;
  background: ${({ theme }) => theme.colors.bgPage};
`;

const CollapsibleSectionTitle = styled(LabelUppercase)`
  color: ${({ theme }) => theme.colors.textPrimary};
  margin: 0;
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0.5px;
  padding: 0;
`;

// Onboarding section with better typography
const OnboardingSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const OnboardingItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const OnboardingLabel = styled(LabelUppercase)`
  color: ${({ theme }) => theme.colors.textPrimary};
  font-size: 10px;
  font-weight: 600;
  margin: 0;
  letter-spacing: 0.5px;
`;

const OnboardingValue = styled(PSmall)`
  color: ${({ theme }) => theme.colors.textRegular};
  margin: 0;
  line-height: 1.5;
  font-size: 13px;
`;

// Learning plan progress with improved design
const LearningPlanProgressSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const OverallProgressCard = styled.div`
  background: ${({ theme }) => theme.colors.bgDefaultSecondary};
  border: 1px solid ${({ theme }) => theme.colors.bgActive};
  border-radius: 8px;
  padding: 16px;
`;

const OverallProgressLabel = styled(LabelUppercase)`
  color: ${({ theme }) => theme.colors.textRegular};
  margin: 0 0 12px 0;
  font-size: 9px;
  font-weight: 600;
  letter-spacing: 0.5px;
`;

const OverallProgressContent = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
`;

const ProgressPercentage = styled(PSmall)`
  margin: 0;
  color: ${({ theme }) => theme.colors.textPrimary};
  font-weight: 700;
  font-size: 18px;
`;

const ProgressBarLarge = styled.div`
  height: 6px;
  flex: 1;
  background-color: ${({ theme }) => theme.colors.bgActive};
  border-radius: 3px;
  overflow: hidden;
`;

const ProgressBarFillLarge = styled.div<{ progress: number }>`
  height: 100%;
  width: ${({ progress }) => progress}%;
  background-color: ${({ theme }) => theme.colors.primary};
  border-radius: 3px;
  transition: width 0.3s ease-in-out;
`;

const LastActiveCard = styled.div`
  background: ${({ theme }) => theme.colors.bgDefaultSecondary};
  border: 1px solid ${({ theme }) => theme.colors.bgActive};
  border-radius: 8px;
  padding: 16px;
`;

const LastActiveLabel = styled(LabelUppercase)`
  color: ${({ theme }) => theme.colors.textRegular};
  margin: 0 0 12px 0;
  font-size: 9px;
  font-weight: 600;
  letter-spacing: 0.5px;
`;

const LastActiveValue = styled(PSmall)`
  margin: 0;
  color: ${({ theme }) => theme.colors.textPrimary};
  font-weight: 600;
  font-size: 14px;
`;

const ProgressGridContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
  margin-bottom: 20px;
`;

const TopicProgressCard = styled.div`
  background: ${({ theme }) => theme.colors.bgPage};
  /* border: 1px solid ${({ theme }) => theme.colors.bgActive}; */
  border-radius: 8px;
  padding: 16px 0;
  margin-bottom: 12px;

  &:last-child {
    margin-bottom: 0;
  }
`;

const TopicHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
`;

const TopicName = styled(TopicText)`
  font-weight: 600;
  font-size: 14px;
  color: inherit;
`;

const TopicDetailsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
  margin-bottom: 16px;
`;

const TopicDetailItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const TopicDetailLabel = styled(LabelUppercase)`
  color: ${({ theme }) => theme.colors.textRegular};
  margin: 0;
  font-size: 8px;
  font-weight: 600;
  letter-spacing: 0.5px;
`;

const TopicDetailValue = styled(PSmall)`
  margin: 0;
  color: ${({ theme }) => theme.colors.textPrimary};
  font-weight: 500;
  font-size: 12px;
`;

const AssessmentRating = styled.div<{ rating: number }>`
  background-color: ${({ theme }) => theme.colors.bgActive};
  color: ${({ theme }) => theme.colors.textPrimary};
  padding: 2px 8px;
  border-radius: 10px;
  font-size: 10px;
  font-weight: 600;
  min-width: 32px;
  text-align: center;
  display: inline-block;
`;

const SubmissionsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const SubmissionItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 12px;
  background: ${({ theme }) => theme.colors.bgDefaultSecondary};
  border-radius: 6px;
  border: 1px solid ${({ theme }) => theme.colors.bgActive};
`;

const SubmissionName = styled(PSmall)`
  margin: 0;
  padding: 0;
  color: ${({ theme }) => theme.colors.textPrimary};
  font-weight: 500;
  flex: 1;
  font-size: 12px;
`;

const SubmissionProgress = styled(PSmall)`
  margin: 0;
  padding: 0;
  color: ${({ theme }) => theme.colors.textRegular};
  font-size: 11px;
  font-weight: 500;
`;

const ReviewStatus = styled.div<{ hasUnreviewed: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 2px;
  font-size: 10px;
  font-weight: 400;
`;

const ReviewLine = styled.div<{ isUnreviewed?: boolean }>`
  line-height: 1.2;
  color: ${({ isUnreviewed, theme }) =>
    isUnreviewed ? theme.colors.secondary : theme.colors.textRegular};
  font-weight: ${({ isUnreviewed }) => (isUnreviewed ? '600' : '400')};
`;

const ComingSoonLabel = styled(PExtraSmall)`
  display: flex;
  margin: 0;
  font-weight: 400;
  color: ${({ theme }) => theme.colors.textRegular};
`;

// Update the main container styling
const MainContainer = styled.div`
  height: 100%;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  background: ${({ theme }) => theme.colors.bgPage};
`;

const StickyHeader = styled.div`
  position: sticky;
  top: 0;
  background-color: ${({ theme }) => theme.colors.bgPage};
  border-bottom: 1px solid ${({ theme }) => theme.colors.bgActive};
  padding: 64px 64px 20px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.02);
`;

const PageTitle = styled(H3)`
  color: ${({ theme }) => theme.colors.textPrimary};
  margin: 0 0 8px 0;
  font-size: 24px;
  font-weight: 700;
`;

const TimezoneSubtitle = styled(LabelUppercase)`
  color: ${({ theme }) => theme.colors.textRegular};
  font-size: 14px;
  margin: 0 0 24px 0;
  font-weight: 500;
`;

const HeaderRow = styled.div`
  display: flex;
  width: 100%;
  justify-content: space-between;
  align-items: flex-end;
  gap: 16px;
`;

const MentorInfoSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const ScrollableContent = styled.div`
  flex: 1;
  overflow: auto;
  padding: 0;
  background: ${({ theme }) => theme.colors.bgPage};
`;

type CollapsibleSectionProps = {
  title: React.ReactNode;
  children: React.ReactNode;
  isOpenByDefault?: boolean;
  style?: React.CSSProperties;
  className?: string;
};

function CollapsibleSection({
  title,
  children,
  isOpenByDefault = false,
  style,
  className,
}: CollapsibleSectionProps) {
  const [isOpen, setIsOpen] = useState(isOpenByDefault);
  const [animationParent] = useAutoAnimate();

  return (
    <CollapsibleSectionContainer style={style} className={className}>
      <CollapsibleHeader
        onClick={() => setIsOpen((prev) => !prev)}
        aria-expanded={isOpen}
        expanded={isOpen}
      >
        <HeaderLeft>
          <ToggleIcon
            className={isOpen ? 'fas fa-chevron-down' : 'fas fa-chevron-right'}
            aria-expanded={isOpen}
          />
          <CollapsibleSectionTitle>{title}</CollapsibleSectionTitle>
        </HeaderLeft>
      </CollapsibleHeader>
      <div ref={animationParent}>
        {isOpen && <CollapsibleContent>{children}</CollapsibleContent>}
      </div>
    </CollapsibleSectionContainer>
  );
}

interface LearnerFormData {
  attended: boolean;
  goals: string;
  challenges: string;
  insights: string;
}

const calculateLearnerProgress = (
  learnerId: number,
  cohortId: number,
  companyData: CompanyResponse | null | undefined,
  submissionsData: SubmissionResponse | null | undefined
): {
  totalProgress: number;
  lastActive: string;
  topicProgress: Array<{
    name: string;
    iconUrl: string;
    selfAssessment: number | undefined;
    reviewStatus: {
      numReviewed: number;
      numUnreviewed: number;
      numRequests: number;
    };
    units: Array<{
      name: string;
      type: 'module' | 'project';
      numSubmitted: number;
      numTotal: number;
    }>;
  }>;
} | null => {
  if (!companyData || !submissionsData) return null;

  const cohortLearner = companyData.cohorts
    .find((c) => c.cycleId === cohortId)
    ?.learners.find((l) => l.learnerId === learnerId);

  // Then find learning plan in company learners
  const companyLearner = companyData.learners.find((l) => l.id === learnerId);

  if (!cohortLearner || !companyLearner) return null;

  // Calculate total progress
  let totalItems = 0;
  let completedItems = 0;

  for (const planItem of companyLearner.learningPlan ?? []) {
    if (planItem.type === LEARNING_PLAN_UNIT_TYPE.CONTENT_MODULE) {
      const coreActivitiesCount = planItem.coreLessonsCount || 0;
      const coreSubmittedCount = planItem.coreSubmittedLessonsCount || 0;
      totalItems += coreActivitiesCount;
      completedItems += Math.min(coreSubmittedCount, coreActivitiesCount);
    } else if (planItem.type === LEARNING_PLAN_UNIT_TYPE.PROJECT) {
      totalItems += 1;
      if ((planItem.submittedExercisesCount || 0) > 0) {
        completedItems += 1;
      }
    }
  }

  const totalProgress =
    totalItems === 0 ? 0 : Math.round((completedItems / totalItems) * 100);

  // Use the real lastActiveAt date
  const lastActive = cohortLearner.lastActiveAt
    ? dayjs(cohortLearner.lastActiveAt).toISOString()
    : '';

  // Group by topics and calculate progress
  const topicGroups = new Map<string, Array<LearningPlanItem>>();

  for (const planItem of companyLearner.learningPlan ?? []) {
    const topicName = planItem.topic;
    if (!topicGroups.has(topicName)) {
      topicGroups.set(topicName, []);
    }
    topicGroups.get(topicName)?.push(planItem);
  }

  const topicProgress = Array.from(topicGroups.entries()).map(
    ([topicName, units]) => {
      const unitDetails = units.map((unit) => {
        if (unit.type === LEARNING_PLAN_UNIT_TYPE.CONTENT_MODULE) {
          return {
            name: unit.content.title,
            type: 'module' as const,
            numSubmitted: unit.coreSubmittedLessonsCount || 0,
            numTotal: unit.coreLessonsCount || 0,
          };
        } else {
          return {
            name: unit.title,
            type: 'project' as const,
            numSubmitted: unit.submittedExercisesCount || 0,
            numTotal: unit.exercisesCount || 0,
          };
        }
      });

      const totalRequests = submissionsData?.submissions?.filter(
        (sub) =>
          sub.unit.topic === topicName &&
          sub.learner.id === learnerId &&
          sub.submission.didRequestMentorReview
      );

      const numReviewed = totalRequests?.filter(
        (sub) => sub.submission.isReviewed
      ).length;

      const selfAssessment =
        cohortLearner?.onboardingAnswers?.skillRatings?.[topicName];

      return {
        name: topicName,
        iconUrl: units[0].topicIconUrl,
        selfAssessment,
        reviewStatus: {
          numReviewed,
          numUnreviewed: totalRequests?.length - numReviewed,
          numRequests: totalRequests?.length,
        },
        units: unitDetails,
      };
    }
  );

  return {
    totalProgress,
    lastActive,
    topicProgress,
  };
};
const useBeforeUnload = (hasUnsavedChanges: boolean) => {
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (hasUnsavedChanges) {
        e.preventDefault();
        e.returnValue = '';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasUnsavedChanges]);
};

export default function SubcohortOfficeHour() {
  const { id: companyIdParam, officeHourId } =
    useParams<{
      id: string;
      officeHourId: string;
    }>();

  const companyId = companyIdParam ? parseInt(companyIdParam) : -1;

  const { data: companyData, refetch: refetchCompany } =
    useGetCompany(companyId);
  const { data: submissionsData, refetch: refetchSubmissions } =
    useGetExerciseSubmissions(companyId);
  const { data: officeHoursData, refetch: refetchOfficeHours } =
    useGetCompanyOfficeHours(companyId);

  const officeHourGroup = officeHoursData?.officeHoursGroups.find((oh) =>
    oh.officeHours.find(
      (oh) => oh.officeHourId === parseInt(officeHourId ?? '0')
    )
  );
  const officeHour = officeHourGroup?.officeHours.find(
    (oh) => oh.officeHourId === parseInt(officeHourId ?? '0')
  );

  const mentor = officeHourGroup?.mentor;
  const members = officeHourGroup?.members;
  const cohort = officeHourGroup?.cohort;
  const { getTokenSilently } = useAuth();

  const [memberNotes, setMemberNotes] = useState<
    Record<number, LearnerFormData>
  >({});

  const hasUnsavedChanges = useMemo(() => {
    const currentData = memberNotes;
    const savedData = officeHour?.members.reduce((acc, member) => {
      acc[member.officeHourNotesId] = {
        attended: member.didAttend ?? false,
        goals: member.notes?.goals ?? '',
        challenges: member.notes?.challenges ?? '',
        insights: member.notes?.insights ?? '',
      };
      return acc;
    }, {} as Record<number, LearnerFormData>);

    return Object.entries(currentData).some(([officeHourNotesId, note]) => {
      const savedNote = savedData?.[parseInt(officeHourNotesId)];
      return (
        note.attended !== savedNote?.attended ||
        note.goals !== savedNote?.goals ||
        note.challenges !== savedNote?.challenges ||
        note.insights !== savedNote?.insights
      );
    });
  }, [memberNotes, officeHour]);
  // Add the beforeunload event handler
  useBeforeUnload(hasUnsavedChanges);

  React.useEffect(() => {
    if (officeHour) {
      setMemberNotes(
        officeHour.members.reduce((acc, member) => {
          acc[member.officeHourNotesId] = {
            attended: member.didAttend ?? false,
            goals: member.notes?.goals ?? '',
            challenges: member.notes?.challenges ?? '',
            insights: member.notes?.insights ?? '',
          };
          return acc;
        }, {} as Record<number, LearnerFormData>)
      );
    }
  }, [officeHour]);

  // Mutation for saving office hour notes
  const saveNotesMutation = useMutation({
    mutationFn: async () => {
      const token = await getTokenSilently();

      const learners: {
        offficeHourNoteId: number;
        didAttend: boolean;
        notes: {
          goals: string;
          challenges: string;
          insights: string;
        };
      }[] = Object.entries(memberNotes).map(([officeHourNotesId, note]) => {
        return {
          offficeHourNoteId: parseInt(officeHourNotesId),
          didAttend: note.attended,
          notes: {
            goals: note.goals,
            challenges: note.challenges,
            insights: note.insights,
          },
        };
      });

      await saveOfficeHoursNotes(token, {
        companyId,
        learners,
      });
    },
    onSuccess: () => {
      toast.success('Notes and attendance saved successfully');
      refetchCompany();
      refetchSubmissions();
      refetchOfficeHours();
    },
    onError: (error) => {
      toast.error(
        error instanceof Error
          ? error.message
          : 'Failed to save notes and attendance'
      );
    },
  });

  // Update specific field for a learner
  const updateLearnerField = (
    learnerId: number,
    field: keyof LearnerFormData,
    value: string | boolean
  ) => {
    setMemberNotes((prev) => ({
      ...prev,
      [learnerId]: {
        ...prev[learnerId],
        [field]: value,
      },
    }));
  };

  // Handle save functionality
  const handleSave = () => {
    saveNotesMutation.mutate();
  };

  const mentorTooltipId = `mentor-tooltip-${mentor?.mentorProfileId}`;

  if (!officeHourGroup) {
    return officeHourId ? (
      <P>Office hour not found.</P>
    ) : (
      <P>Invalid office hour ID.</P>
    );
  }

  const officeHourTimestamp = officeHour?.timestamp;
  const cohortStartDate = officeHourGroup?.cohort.startDate;
  const localTimezone = dayjs.tz.guess();
  const weekNumber =
    Math.floor(
      dayjs.utc(officeHourTimestamp).diff(cohortStartDate, 'day') / 7
    ) + 1;

  const headerText = officeHour
    ? `Week ${weekNumber} Call - ${dayjs
        .utc(officeHourTimestamp)
        .tz(localTimezone)
        .format('D MMM YYYY HH:mm')}`
    : 'Office Hour';

  return (
    <MainContainer>
      <Prompt
        when={hasUnsavedChanges}
        message="You have unsaved changes. Are you sure you want to leave this page?"
      />
      <StickyHeader>
        <PageTitle>{headerText}</PageTitle>
        <TimezoneSubtitle>{localTimezone}</TimezoneSubtitle>
        <HeaderRow>
          <MentorInfoSection>
            <MentorLabel>Mentor</MentorLabel>
            <MentorName>
              <span data-tooltip-id={mentorTooltipId}>{mentor?.name}</span>
            </MentorName>
          </MentorInfoSection>

          <div>
            <Button
              style={{ marginTop: 0 }}
              label={'Save Office Hour Notes'}
              type="primary"
              onClick={handleSave}
              isDisabled={saveNotesMutation.isLoading || !hasUnsavedChanges}
              isLoading={saveNotesMutation.isLoading}
              showIconBeforeLabel
              faIcon="fa-duotone fa-save"
            />
          </div>
        </HeaderRow>
      </StickyHeader>

      <ScrollableContent>
        <LearnersContainer>
          {(members ?? []).map((member) => {
            const officeHourNotesId = officeHour?.members.find(
              (m) => m.memberId === member.id
            )?.officeHourNotesId;
            if (!officeHourNotesId) {
              return null;
            }
            const currentData = memberNotes[officeHourNotesId] || {
              attended: false,
              goals: '',
              challenges: '',
              insights: '',
            };

            const learnerTooltipId = `learner-tooltip-${member.id}`;

            const cohortLearner = companyData?.cohorts
              .find((c) => c.cycleId === cohort?.id)
              ?.learners.find((l) => l.learnerId === member.learnerId);

            const companyLearner = companyData?.learners.find(
              (l) => l.id === member.learnerId
            );

            const progressData = calculateLearnerProgress(
              member.learnerId,
              cohort?.id ?? -1,
              companyData,
              submissionsData
            );
            return (
              <LearnerCard key={member.id}>
                <LearnerHeader>
                  <MentorLabel>
                    {companyLearner?.onboardingAnswers.role ?? 'Unknown role'}
                  </MentorLabel>
                  <LearnerNameRow>
                    <MentorName>
                      <span data-tooltip-id={learnerTooltipId}>
                        {member.name}
                      </span>
                    </MentorName>
                  </LearnerNameRow>
                </LearnerHeader>

                <LearnerContent>
                  <SectionContainer>
                    <SectionHeader>
                      <SectionTitle>Office Hour Attendance</SectionTitle>
                    </SectionHeader>
                    <SectionContent>
                      <AttendanceContent>
                        <Checkbox
                          isChecked={currentData.attended}
                          onChange={(e) =>
                            updateLearnerField(
                              officeHourNotesId,
                              'attended',
                              e.target.checked
                            )
                          }
                        />
                        <CheckboxLabel>Mark as attended</CheckboxLabel>
                      </AttendanceContent>
                    </SectionContent>
                  </SectionContainer>

                  <SectionContainer>
                    <SectionHeader>
                      <SectionTitle>Session Notes</SectionTitle>
                    </SectionHeader>
                    <SectionContent>
                      <NotesGrid>
                        <NoteField>
                          <NoteLabel>Learner Goals</NoteLabel>
                          <CompactTextArea
                            placeholder="What are this learner's goals for this session?"
                            value={currentData.goals}
                            onChange={(e) =>
                              updateLearnerField(
                                officeHourNotesId,
                                'goals',
                                e.target.value
                              )
                            }
                            minRows={2}
                            maxRows={2}
                          />
                        </NoteField>

                        <NoteField>
                          <NoteLabel>Challenges</NoteLabel>
                          <CompactTextArea
                            placeholder="What challenges is this learner facing?"
                            value={currentData.challenges}
                            onChange={(e) =>
                              updateLearnerField(
                                officeHourNotesId,
                                'challenges',
                                e.target.value
                              )
                            }
                            minRows={2}
                            maxRows={2}
                          />
                        </NoteField>

                        <NoteField>
                          <NoteLabel>Key Insights</NoteLabel>
                          <CompactTextArea
                            placeholder="Key insights or observations about this learner"
                            value={currentData.insights}
                            onChange={(e) =>
                              updateLearnerField(
                                officeHourNotesId,
                                'insights',
                                e.target.value
                              )
                            }
                            minRows={2}
                            maxRows={2}
                          />
                        </NoteField>
                      </NotesGrid>
                    </SectionContent>
                  </SectionContainer>

                  <CollapsibleSection title="Onboarding Answers">
                    <OnboardingSection>
                      <OnboardingItem>
                        <OnboardingLabel>
                          Common Tasks & Workflows
                        </OnboardingLabel>
                        <OnboardingValue>
                          {cohortLearner?.onboardingAnswers.commonTasks ||
                            'No information provided'}
                        </OnboardingValue>
                      </OnboardingItem>

                      <OnboardingItem>
                        <OnboardingLabel>Additional Comments</OnboardingLabel>
                        <OnboardingValue>
                          {cohortLearner?.onboardingAnswers
                            .additionalComments ||
                            'No additional comments provided'}
                        </OnboardingValue>
                      </OnboardingItem>
                    </OnboardingSection>
                  </CollapsibleSection>

                  <CollapsibleSection
                    title="Activity & Progress"
                    isOpenByDefault={true}
                  >
                    <LearningPlanProgressSection>
                      {(() => {
                        if (!progressData) {
                          return (
                            <PSmall>No learning plan data available</PSmall>
                          );
                        }

                        return (
                          <>
                            <ProgressGridContainer>
                              <OverallProgressCard>
                                <OverallProgressLabel>
                                  Overall Progress
                                </OverallProgressLabel>
                                <OverallProgressContent>
                                  <ProgressPercentage>
                                    {progressData.totalProgress}%
                                  </ProgressPercentage>
                                  <ProgressBarLarge>
                                    <ProgressBarFillLarge
                                      progress={progressData.totalProgress}
                                    />
                                  </ProgressBarLarge>
                                </OverallProgressContent>
                              </OverallProgressCard>

                              <LastActiveCard>
                                <LastActiveLabel>Last Active</LastActiveLabel>
                                <LastActiveValue>
                                  {progressData.lastActive ? (
                                    <div
                                      data-tooltip-id={`last-active-tooltip-${member.id}`}
                                    >
                                      {dayjs(progressData.lastActive).fromNow()}
                                      <SmallTooltip
                                        anchorSelect={`[data-tooltip-id="last-active-tooltip-${member.id}"]`}
                                      >
                                        {dayjs(progressData.lastActive).format(
                                          'MMM D, YYYY HH:mm UTC'
                                        )}
                                      </SmallTooltip>
                                    </div>
                                  ) : (
                                    '-'
                                  )}
                                </LastActiveValue>
                              </LastActiveCard>
                            </ProgressGridContainer>

                            {progressData.topicProgress.map((topic, index) => (
                              <TopicProgressCard key={index}>
                                <TopicHeader>
                                  <TopicPill
                                    backgroundColor={
                                      TOPIC_TO_COLOR[
                                        topic.name as keyof typeof TOPIC_TO_COLOR
                                      ] || '#666'
                                    }
                                  >
                                    <TopicIcon src={topic.iconUrl} />
                                    <TopicName>{topic.name}</TopicName>
                                  </TopicPill>
                                </TopicHeader>

                                <TopicDetailsGrid>
                                  <TopicDetailItem>
                                    <TopicDetailLabel>
                                      Onboarding Self Assessment
                                    </TopicDetailLabel>
                                    <TopicDetailValue>
                                      {(() => {
                                        const skillRating =
                                          topic.selfAssessment;
                                        const displayRating =
                                          skillRating !== undefined
                                            ? `${skillRating}/10`
                                            : 'Unknown';
                                        return (
                                          <AssessmentRating
                                            rating={skillRating ?? 0}
                                          >
                                            {displayRating}
                                          </AssessmentRating>
                                        );
                                      })()}
                                    </TopicDetailValue>
                                  </TopicDetailItem>

                                  <TopicDetailItem>
                                    <TopicDetailLabel>
                                      SOS questions
                                    </TopicDetailLabel>
                                    <TopicDetailValue>
                                      {(() => {
                                        if (
                                          topic.reviewStatus.numRequests === 0
                                        ) {
                                          return (
                                            <ReviewStatus hasUnreviewed={false}>
                                              <ReviewLine isUnreviewed={false}>
                                                0 questions
                                              </ReviewLine>
                                            </ReviewStatus>
                                          );
                                        }

                                        return (
                                          <ReviewStatus
                                            hasUnreviewed={
                                              topic.reviewStatus.numUnreviewed >
                                              0
                                            }
                                          >
                                            {topic.reviewStatus.numReviewed >
                                              0 && (
                                              <ReviewLine isUnreviewed={false}>
                                                {topic.reviewStatus.numReviewed}{' '}
                                                replied
                                              </ReviewLine>
                                            )}
                                            {topic.reviewStatus.numUnreviewed >
                                              0 && (
                                              <ReviewLine isUnreviewed={true}>
                                                {
                                                  topic.reviewStatus
                                                    .numUnreviewed
                                                }{' '}
                                                pending reply
                                              </ReviewLine>
                                            )}
                                          </ReviewStatus>
                                        );
                                      })()}
                                    </TopicDetailValue>
                                  </TopicDetailItem>
                                </TopicDetailsGrid>

                                <SubmissionsList>
                                  {topic.units.map((unit, unitIndex) => (
                                    <SubmissionItem key={unitIndex}>
                                      <SubmissionName>
                                        {unit.name}
                                      </SubmissionName>
                                      <SubmissionProgress>
                                        {unit.numSubmitted}/{unit.numTotal}{' '}
                                        {unit.type === 'module'
                                          ? 'activities'
                                          : 'exercises'}
                                      </SubmissionProgress>
                                    </SubmissionItem>
                                  ))}
                                </SubmissionsList>
                              </TopicProgressCard>
                            ))}
                          </>
                        );
                      })()}
                    </LearningPlanProgressSection>
                  </CollapsibleSection>
                  <CollapsibleSection title="Suggested Agenda & Discussion Points">
                    <ComingSoonLabel>Coming Soon...</ComingSoonLabel>
                  </CollapsibleSection>
                </LearnerContent>
              </LearnerCard>
            );
          })}
        </LearnersContainer>
      </ScrollableContent>
      <SmallPortalTooltip
        anchorSelect={`[data-tooltip-id="${mentorTooltipId}"]`}
      >
        <TooltipContent>
          <TooltipDetail>{mentor?.email}</TooltipDetail>
        </TooltipContent>
      </SmallPortalTooltip>
      {members?.map((learner) => (
        <SmallPortalTooltip
          anchorSelect={`[data-tooltip-id="learner-tooltip-${learner.id}"]`}
        >
          <TooltipContent>
            <TooltipDetail>{learner.email}</TooltipDetail>
          </TooltipContent>
        </SmallPortalTooltip>
      ))}
    </MainContainer>
  );
}
